import { Dispatch, PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
    AccessDto,
    MapProjectItem,
    MapProjectResult,
    ProjectDto,
    ProjectFilterInput,
    ProjectSearchType,
    ProjectService,
} from "../../../api/psc";
import {
    projectUpdatedAction,
    projectVisitedAction,
} from "../../../slices/SharedActions";
import {
    clearSelectedProjects,
    setProjectDetailIndex,
} from "../../../slices/SharedSlice";
import { detectFilterChange } from "../../../utils/filter";
import { createRandomId } from "../../../utils/random";

export interface MapState {
    id: string;
    page: number;
    isLoading: boolean;
    points: MapProjectItem[];
    filter: ProjectFilterInput;
    totalCount: number;
    hasAnyFilter: boolean;
}

const initialState: MapState = {
    id: "",
    page: 0,
    isLoading: true,
    points: [],
    filter: {
        type: ProjectSearchType.ALL,
        onlyWithConstructor: true,
        cityIds: [],
        regionIds: [],
        subRegionIds: [],
        phaseIds: [],
        folderIds: [],
        usageTypesIds: [],
        structureTypeIds: [],
    },
    totalCount: 0,
    hasAnyFilter: false,
};

const mapSlice = createSlice({
    name: "map",
    initialState,
    reducers: {
        setInitialMapAccess(
            state: MapState,
            action: PayloadAction<AccessDto | null>
        ) {
            const filter = { ...state.filter };

            if (action.payload) {
                filter.targetAccessId = action.payload.id;
            } else {
                filter.type = ProjectSearchType.ALL;
            }

            return {
                ...initialState,
                filter: filter,
            };
        },
        setMapPoints(state: MapState, action: PayloadAction<MapProjectResult>) {
            return {
                ...state,
                isLoading: false,
                points: action.payload.projects,
            };
        },
        setMapFilter(
            state: MapState,
            action: PayloadAction<ProjectFilterInput>
        ) {
            const isTargetAccessChanged =
                state.filter.targetAccessId !== action.payload.targetAccessId;
            const isTypeChanged = state.filter.type !== action.payload.type;

            const id =
                isTypeChanged || isTargetAccessChanged
                    ? createRandomId(5)
                    : state.id;
            const page =
                isTypeChanged || isTargetAccessChanged ? 0 : state.page;
            const isLoading = isTypeChanged || isTargetAccessChanged;

            return {
                ...state,
                filter: action.payload,
                hasAnyFilter: detectFilterChange(
                    initialState.filter,
                    action.payload
                ),
                isLoading: isLoading,
                id: id,
                page: page,
            };
        },
        reloadMap(state: MapState, action: PayloadAction) {
            return {
                ...state,
                isLoading: true,
                id: createRandomId(5),
            };
        },
        resetMapFilter(state: MapState, action: PayloadAction) {
            return {
                ...state,
                hasAnyFilter: false,
                filter: { ...initialState.filter },
            };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(
            projectUpdatedAction,
            (s: MapState, a: PayloadAction<ProjectDto>) => {
                const project = s.points.find(
                    (p) => p.hashId === a.payload.hashId
                );
                const updatedProjectList = [...s.points];

                if (project) {
                    const indexOfProject = updatedProjectList.indexOf(project);
                    updatedProjectList[indexOfProject] = a.payload;
                }

                return { ...s, points: updatedProjectList };
            }
        );
        builder.addCase(
            projectVisitedAction,
            (s: MapState, a: PayloadAction<string>) => {
                const project = s.points.find((p) => p.hashId === a.payload);
                const updatedProjectList = [...s.points];

                if (project) {
                    const index = updatedProjectList.indexOf(project);
                    updatedProjectList[index] = { ...project, isVisited: true };
                }

                return {
                    ...s,
                    points: updatedProjectList,
                };
            }
        );
    },
});

export function FetchPoints(state: MapState, dispatch: Dispatch<any>) {
    ProjectService.getMapProjects("1", state.filter).then((r) => {
        dispatch(setMapPoints(r));
        dispatch(clearSelectedProjects());
        dispatch(setProjectDetailIndex(0));
    });
}

export const {
    setInitialMapAccess,
    setMapPoints,
    setMapFilter,
    reloadMap,
    resetMapFilter,
} = mapSlice.actions;
export default mapSlice.reducer;
